import { ReactNode, useCallback, useEffect, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { useLocation } from 'react-router-dom'

import { useGetQRAAssessmentQuery } from 'app/services/api/qra/assessments'
import LoadingSpinner from 'common/components/LoadingSpinner'
import DetailsModal from 'common/components/Modal/DetailsModal'

import { IStateAssessment } from 'features/RiskManagement/Assessments'
import RunScenarioConfirmationModal from 'features/RiskManagement/Assessments/Scenarios/RunScenarioConfirmationModal'

import { ActionBar } from './ActionBar'

// The 500ms handoff delay is necessary to ensure that the modal transition animation
// completes before triggering the next UI update. Without this delay, users may experience
// abrupt content changes or flickering, especially when rapidly confirming or closing the modal.

interface IWISModal {
  isModalOpen: boolean
  closeModal: (pathnameOverride?: string) => void
  canConfirm: boolean
  onConfirm: () => Promise<void> | void
  renderContent: ReactNode
  cancelButton?: ReactNode
  assessmentRan?: IStateAssessment
  footerApplyLabel: string
  onCancel: () => void
  isLoading: boolean
  showRunConfirm?: boolean
}

const WISModal = ({
  isModalOpen,
  closeModal,
  canConfirm,
  onConfirm,
  onCancel,
  renderContent,
  cancelButton,
  assessmentRan,
  footerApplyLabel,
  isLoading,
  showRunConfirm = true
}: IWISModal) => {
  const { pathname } = useLocation()
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [shouldPrefetchAssessment, setShouldPrefetchAssessment] =
    useState(false)
  // track the confirmation process
  const [isConfirmationInProgress, setIsConfirmationInProgress] =
    useState(false)

  const closeModalAndClearAssessmentCache = useCallback<typeof closeModal>(
    (...args) => {
      /**
       * AssessmentRan variable purpose was to provide WIS id
       * to allow redirect via WIS popup.
       * It will not be needed after closing that popup.
       */
      setIsConfirmModalOpen(false)
      setShouldPrefetchAssessment(false)
      setIsConfirmationInProgress(false)

      closeModal(...args)
    },
    [closeModal]
  )

  const navigateToAssessmentRanAndCloseModal = useCallback(() => {
    if (assessmentRan == null) {
      console.error('No assessment to navigate to!')

      return
    }

    const rmParentPath = pathname.slice(0, pathname.indexOf('/risk-management'))

    closeModalAndClearAssessmentCache(
      `${rmParentPath}/risk-management/assessments/states/${assessmentRan.savedStateId}/assessments/${assessmentRan?.id}`
    )
  }, [assessmentRan, closeModalAndClearAssessmentCache, pathname])

  // when the backend returns the created/rerun assessment, jump immediately
  useEffect(() => {
    if (assessmentRan != null && isModalOpen) {
      setShouldPrefetchAssessment(true)
    }
  }, [assessmentRan, isModalOpen])

  const assessmentIdForQuery =
    shouldPrefetchAssessment && assessmentRan != null
      ? String(assessmentRan.id)
      : skipToken

  const { data: readyAssessment, isFetching: isFetchingAssessment } =
    useGetQRAAssessmentQuery(assessmentIdForQuery, {
      // gentle polling; adjust as needed
      pollingInterval: 700,
      refetchOnFocus: false,
      refetchOnReconnect: true
    })

  useEffect(() => {
    if (shouldPrefetchAssessment && readyAssessment != null) {
      // Small delay to ensure Redux cache is updated before navigation
      // This prevents the error page flash during route transition
      const navigationTimer = setTimeout(() => {
        navigateToAssessmentRanAndCloseModal()
        setShouldPrefetchAssessment(false)
        setIsConfirmationInProgress(false)
      }, 100) // Minimal delay just for cache propagation

      return () => clearTimeout(navigationTimer)
    }
  }, [
    shouldPrefetchAssessment,
    readyAssessment,
    navigateToAssessmentRanAndCloseModal
  ])

  const handleOpenConfirmation = useCallback(() => {
    if (showRunConfirm) {
      setIsConfirmModalOpen(true)
    } else {
      onConfirm()
    }
  }, [showRunConfirm, onConfirm])

  const handleCancelConfirmation = useCallback(() => {
    setIsConfirmModalOpen(false)
    setIsConfirmationInProgress(false)
  }, [])

  const handleConfirmAndRun = useCallback(async () => {
    setIsConfirmationInProgress(true)
    setIsConfirmModalOpen(false)

    try {
      await Promise.resolve(onConfirm())
    } catch (error) {
      console.error('WIS run failed to start:', error)
      // Clear confirmation progress on error
      setIsConfirmationInProgress(false)
    }
  }, [onConfirm])

  const confirmDescription = [
    'Review the details of your What-If Scenario. Once you click Confirm, the scenario will begin.',
    'Depending on the scope, processing may take up to 8 hours. You will be notified upon completion.'
  ]

  const effectiveLoading =
    isLoading ||
    isConfirmationInProgress ||
    (shouldPrefetchAssessment && isFetchingAssessment)

  const handleCancel = useCallback(() => {
    // Prevent cancellation during loading/confirmation process
    if (effectiveLoading) {
      return
    }

    setIsConfirmModalOpen(false)
    setIsConfirmationInProgress(false)
    onCancel()
  }, [effectiveLoading, onCancel])

  const footer = (
    <ActionBar
      canConfirm={canConfirm && !effectiveLoading}
      onConfirm={handleOpenConfirmation}
      onCancel={handleCancel}
    >
      {footerApplyLabel}
    </ActionBar>
  )

  return (
    <>
      <DetailsModal
        title={cancelButton}
        open={isModalOpen}
        close={closeModalAndClearAssessmentCache}
        footer={footer}
        disableClose={effectiveLoading}
      >
        <div className='m-auto w-min overflow-auto bg-white'>
          <LoadingSpinner isLoading={effectiveLoading}>
            {renderContent}
          </LoadingSpinner>
        </div>
      </DetailsModal>
      <RunScenarioConfirmationModal
        isModalOpen={isConfirmModalOpen}
        title='Confirm What-If Scenario Run'
        description={confirmDescription}
        confirmLabel='CONFIRM'
        cancelLabel='CANCEL'
        onConfirm={handleConfirmAndRun}
        onCancel={handleCancelConfirmation}
        isLoading={effectiveLoading} // keep spinner on through prefetch
      />
    </>
  )
}

export default WISModal
